import { useState, useEffect, useCallback } from 'react';
import { getInitiativeTemplates } from '@/lib/api/initiatives';
import type { InitiativeTemplate } from '@/types/initiative';

export interface UseInitiativeTemplatesReturn {
  templates: InitiativeTemplate[];
  loading: boolean;
  error: string | null;
}

export function useInitiativeTemplates(
  category?: string
): UseInitiativeTemplatesReturn {
  const [allTemplates, setAllTemplates] = useState<InitiativeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInitiativeTemplates();
      if (response.success && response.data) {
        setAllTemplates(response.data);
      } else {
        setError(response.message || 'Failed to load templates');
        setAllTemplates([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setAllTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const templates = category && category !== 'all'
    ? allTemplates.filter((tpl) => tpl.category === category)
    : allTemplates;

  return { templates, loading, error };
}
